import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'

import { cn } from '@gto/ui/cn'
import { Popover } from '@gto/ui/popover'
import { Tooltip } from '@gto/ui/tooltip'
import { typography } from '@gto/ui/typography'

import type { ChipSize } from './chip'
import { Chip } from './chip'

type ChipGroupOption<T extends string = string> = {
  value: T
  label: ReactNode
  disabled?: boolean
  tooltip?: ReactNode
  /** Renders disabled-option help in a keyboard-navigable popover instead of a tooltip. */
  tooltipInteractive?: boolean
  /**
   * Marks this option as the "exclusive reset" in a multi-select group (typically the `Any` chip).
   * When selected alone, it represents the cleared / no-filter state and the group renders
   * without the multi-select checkmark layout. Selecting any non-exclusive option removes
   * the exclusive value from the selection; selecting the exclusive option clears the rest.
   * Has no effect in single-select mode.
   */
  exclusive?: boolean
}

type SingleChipGroupProps<T extends string = string> = {
  options: ChipGroupOption<T>[]
  value: T | null
  onChange: (value: T | null) => void
  multiple?: false
  visibleCount?: number
  size?: ChipSize
  disabled?: boolean
  showSelectedWhenDisabled?: boolean
  className?: string
  chipClassName?: string
  disableChipHover?: boolean
  'data-tst'?: string
}

type MultiChipGroupProps<T extends string = string> = {
  options: ChipGroupOption<T>[]
  value: T[]
  onChange: (value: T[]) => void
  multiple: true
  visibleCount?: number
  size?: ChipSize
  disabled?: boolean
  showSelectedWhenDisabled?: boolean
  className?: string
  chipClassName?: string
  disableChipHover?: boolean
  'data-tst'?: string
}

type ToggleChipGroupProps<T extends string = string> =
  | SingleChipGroupProps<T>
  | MultiChipGroupProps<T>

type InteractiveTooltipProps = {
  children: ReactNode
  content: ReactNode
}

const InteractiveTooltip = ({ children, content }: InteractiveTooltipProps) => {
  const [open, setOpen] = useState(false)

  return (
    <Popover.Root
      open={open}
      onOpenChange={setOpen}
    >
      <Popover.Trigger
        aria-disabled
        openOnHover
        className="inline-flex focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-main"
        closeDelay={150}
        nativeButton={false}
        render={<span>{children}</span>}
        onFocus={() => setOpen(true)}
      />
      <Popover.Portal>
        <Popover.Positioner
          className="z-tooltip"
          side="top"
          sideOffset={8}
        >
          <Popover.Popup
            initialFocus={false}
            className={cn(
              typography['tooltip/label-small'],
              'max-w-80 rounded-sm border border-secondary-main bg-paper-elevation4 p-2 text-text-primary shadow-8',
            )}
          >
            {content}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

const ToggleChipGroup = <T extends string>(props: ToggleChipGroupProps<T>) => {
  const {
    options,
    visibleCount,
    size = 'medium',
    disabled = false,
    showSelectedWhenDisabled = false,
    className,
    chipClassName,
    disableChipHover = false,
    'data-tst': dataTst,
  } = props

  const [isExpanded, setIsExpanded] = useState(false)

  const visibleOptions =
    !isExpanded && visibleCount != null && options.length > visibleCount
      ? options.slice(0, visibleCount)
      : options
  const hiddenCount = options.length - visibleOptions.length

  const exclusiveValue = options.find((o) => o.exclusive === true)?.value

  const isSelected = (optionValue: T): boolean => {
    if (props.multiple === true) {
      return props.value.includes(optionValue)
    }

    return props.value === optionValue
  }

  const handleClick = useCallback(
    (optionValue: T) => {
      if (disabled || options.find((o) => o.value === optionValue)?.disabled === true) {
        return
      }

      if (props.multiple === true) {
        if (optionValue === exclusiveValue) {
          // Clicking the exclusive option clears any real selection.
          // No-op if it was already the only thing selected.
          if (props.value.length === 1 && props.value[0] === optionValue) {
            return
          }
          props.onChange([optionValue])

          return
        }

        const withoutExclusive = props.value.filter((v) => v !== exclusiveValue)
        const selected = withoutExclusive.includes(optionValue)
        const next = selected
          ? withoutExclusive.filter((v) => v !== optionValue)
          : [...withoutExclusive, optionValue]

        // Falling back to the exclusive option keeps the group in the "reset" state
        // rather than emitting an empty selection.
        if (next.length === 0 && exclusiveValue !== undefined) {
          props.onChange([exclusiveValue])

          return
        }

        props.onChange(next)
      } else {
        const selected = props.value === optionValue
        props.onChange(selected ? null : optionValue)
      }
    },
    [disabled, props, options, exclusiveValue],
  )

  const handleExpand = useCallback(() => {
    setIsExpanded(true)
  }, [])

  return (
    <div
      className={cn('flex flex-wrap gap-2', className)}
      data-tst={dataTst}
    >
      {visibleOptions.map((option) => {
        const isOptionDisabled = disabled || option.disabled === true
        const isInteractiveTooltip =
          isOptionDisabled && option.tooltip != null && option.tooltipInteractive === true
        const shouldHidePrimary = disabled && !showSelectedWhenDisabled
        const isMultiSelect = props.multiple === true && (size === 'medium' || size === 'large')
        const chip = isInteractiveTooltip ? (
          <Chip
            color="default"
            disabled={isOptionDisabled}
            exclusive={option.exclusive === true}
            label={option.label}
            multiSelect={isMultiSelect}
            size={size}
            variant="outlined"
            className={cn(
              chipClassName,
              disableChipHover && 'hover:!bg-transparent hover:!shadow-none',
            )}
          />
        ) : (
          <Chip
            color={!shouldHidePrimary && isSelected(option.value) ? 'primary' : 'default'}
            disabled={isOptionDisabled}
            exclusive={option.exclusive === true}
            label={option.label}
            multiSelect={isMultiSelect}
            size={size}
            variant="outlined"
            className={cn(
              chipClassName,
              disableChipHover && 'hover:!bg-transparent hover:!shadow-none',
            )}
            onClick={() => handleClick(option.value)}
          />
        )

        if (isInteractiveTooltip) {
          return (
            <InteractiveTooltip
              key={option.value}
              content={option.tooltip}
            >
              {chip}
            </InteractiveTooltip>
          )
        }

        return (
          <Tooltip
            key={option.value}
            content={option.tooltip}
          >
            {/* `Chip` does not forward a ref, so Base UI's tooltip trigger cannot attach
                to it directly — wrap it in a ref-forwarding span whenever a tooltip is
                present so the tooltip works for enabled chips too (not just disabled,
                which already needs the span to stay hoverable under pointer-events). */}
            {option.tooltip == null ? (
              chip
            ) : (
              <span className={cn('inline-flex', isOptionDisabled && 'pointer-events-auto')}>
                {chip}
              </span>
            )}
          </Tooltip>
        )
      })}
      {hiddenCount > 0 && (
        <Chip
          color="default"
          label={`+${hiddenCount}`}
          size={size}
          variant="filled"
          onClick={handleExpand}
        />
      )}
    </div>
  )
}

export { ToggleChipGroup }
export type { ChipGroupOption, ToggleChipGroupProps }
